import { AssignableOfficial, HearingOfficialAssignment, HearingOfficialAssignmentService } from "../../hearing-detail/hearing-official-assignment.service";
import { CommonModule } from "@angular/common";
import { EventEmitter, Output, Component, DestroyRef, ElementRef, Injector, Input, OnInit, Signal, TemplateRef, afterNextRender, computed, effect, inject, signal, viewChild } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { OverlayContainer } from "@angular/cdk/overlay";
import { EzDialogComponent } from "@template-ui/components/ez-dialog/ez-dialog.component";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { finalize, forkJoin, Observable, Subscription } from "rxjs";
import { ColDef, GridOptions, ICellRendererParams } from "ag-grid-community";
import { UiLibraryModule } from "@template-ui/template-ui.module";
import { EzFormInputType, InputChangeEvent, InputConfig } from "@template-ui/types/ez-form.types";
import { TemplateRendererComponent } from "@app/shared/components/tables/ag-grid-cells/template-renderer/template-renderer.component";
import { TerminologyIdentityDirective } from "@app/shared/terminology/terminology-identity.directive";
import { createTerminologyUsageSignals } from "@app/shared/terminology/terminology-usage-signals";
import { PRE_HEARING_PARTICIPANT_ROSTER_TERMINOLOGY } from "./pre-hearing-participant-roster.terminology";
import { editorForm, noticeParticipantFields, selectField, textField } from "./participant-notification-forms";
import { TerminologyFormIdentityDirective } from "@app/shared/terminology/terminology-form-identity.directive";
import { RoleService } from "@app/shared/services/roles.service";
import { ADDRESS_COUNTRIES, addressCountryCode, addressRegions, normalizeAddressCountry, normalizeAddressRegion } from "@app/shared/utils/address-lookups";
import { HearingRecordDto } from "../hearing-record.models";
import { HearingNoticeWorkspaceService, NoticeParticipant, NoticeWorkspace, ParticipantNotice, ParticipantMailingAddress } from "../hearing-notice-workspace.service";

@Component({
  selector: "phms-pre-hearing-victim-notification-section",
  standalone: true,
  imports: [CommonModule, UiLibraryModule, TerminologyIdentityDirective, TerminologyFormIdentityDirective],
  templateUrl: "./pre-hearing-victim-notification-section.component.html",
  styleUrl: "./pre-hearing-victim-notification-section.component.scss",
})
export class PreHearingVictimNotificationSectionComponent implements OnInit {
  @Input({ required: true }) record!: Signal<HearingRecordDto | null>;
  readonly previewNoticeId = signal<number | null>(null);
  private readonly api = inject(HearingNoticeWorkspaceService);
  private readonly roles = inject(RoleService);
  private readonly officialsApi = inject(HearingOfficialAssignmentService);
  readonly canReadOfficials = this.roles.hasAnyPermissionSignal("workflow_runtime.read");
  private readonly assignPermission = this.roles.hasAnyPermissionSignal("workflow_runtime.assign");
  readonly canAssignOfficials = computed(() => this.canReadOfficials() && this.assignPermission());
  readonly officials = signal<HearingOfficialAssignment[]>([]);
  readonly eligibleOfficials = signal<AssignableOfficial[]>([]);
  readonly officialsLoading = signal(false);
  readonly officialsError = signal("");
  readonly officialSaveError = signal(false);
  readonly officialEditor = signal(false);
  private editingOfficialVersion: HearingOfficialAssignment[] = [];
  readonly officialPeople = computed<NoticeParticipant[]>(() => this.officials().map(item => ({
    id: -item.id, officialUserId: item.officialUserId, isPresiding: !!item.isPresiding,
    displayName: item.officialDisplayName, roleCode: "parole_official", email: item.officialEmail ?? "", phone: "", postalAddress: "",
    isActive: true, version: item.version ?? 1, relationships: [],
  })));
  readonly allPeople = computed(() => [...this.officialPeople(), ...this.workspace().participants]);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly proceedingId = computed(() => this.record?.()?.scheduledProceedingId ?? null);
  private readonly sensitive = this.roles.hasAnyPermissionSignal("victim_support_record.read_sensitive");
  private readonly author = this.roles.hasAnyPermissionSignal("victim_support_record.author");
  private readonly reader = this.roles.hasAnyPermissionSignal("victim_support_record.read");
  readonly canRead = computed(() => this.sensitive() && this.reader());
  readonly canEdit = computed(() => this.canRead() && this.author());
  readonly workspace = signal<NoticeWorkspace>({ participants: [], notices: [], responses: [] });
  readonly selectedId = signal<number | null>(null);
  readonly selected = computed(() => this.workspace().participants.find(p => p.id === this.selectedId()) ?? null);
  readonly showInactive = signal(false);
  readonly historyId = signal<number | null>(null);
  readonly visiblePeople = computed(() => this.allPeople().filter(p => p.isActive || this.showInactive()));
  readonly activePeople = computed(() => this.workspace().participants.filter(p => p.isActive));
  readonly notices = computed(() => this.workspace().notices.filter(n => n.participantId === this.selectedId() || n.onBehalfOfParticipantId === this.selectedId()));
  readonly responses = computed(() => this.workspace().responses.filter(r => r.participantId === this.selectedId() || r.onBehalfOfParticipantId === this.selectedId()));
  readonly loading = signal(false);
  readonly loadFailed = signal(false);
  @Output() readonly busyChanged = new EventEmitter<boolean>();
  readonly busy = signal(false);
  readonly error = signal("");
  readonly message = signal("");
  readonly mode = signal<"view" | "participant" | "relationships" | "notice" | "response">("view");
  readonly terminologyUsage = PRE_HEARING_PARTICIPANT_ROSTER_TERMINOLOGY;
  readonly text = createTerminologyUsageSignals(this.terminologyUsage);
  readonly participantFormUsage = { firstName: this.terminologyUsage.firstName, lastName: this.terminologyUsage.lastName };
  readonly officialFormUsage = { officialUserId: this.terminologyUsage.officialPicker };
  readonly responseFormUsage = { noticeId: this.terminologyUsage.noticeRelated };
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly dialog = inject(MatDialog);
  private readonly overlayContainer = inject(OverlayContainer);
  private readonly relationshipEditor = viewChild<TemplateRef<unknown>>("relationshipEditor");
  private readonly responseEditor = viewChild<TemplateRef<unknown>>("responseEditor");
  private readonly noticeEditor = viewChild<TemplateRef<unknown>>("noticeEditor");
  private readonly relationshipsCell = viewChild<TemplateRef<unknown>>("relationshipsCell");
  private readonly personEditor = viewChild<TemplateRef<unknown>>("personEditor");
  private editorDialog: MatDialogRef<EzDialogComponent> | null = null;
  private editorSubscriptions = new Subscription();
  private statusDialog: MatDialogRef<EzDialogComponent> | null = null;
  readonly statusConfirmation = signal<ParticipantNotice | null>(null);
  private readonly statusConfirmationContent = viewChild<TemplateRef<unknown>>("statusConfirmationContent");
  participantConfig: InputConfig[] = [];
  addressConfig: InputConfig[] = [];
  participantForm: FormGroup = new FormGroup({});
  addressForm: FormGroup = new FormGroup({});
  responseConfig: InputConfig[] = [];
  responseForm: FormGroup = new FormGroup({});
  noticeConfig: InputConfig[] = [];
  noticeForm: FormGroup = new FormGroup({});
  relationshipEditors: { link: NoticeParticipant['relationships'][number]; form: FormGroup; config: InputConfig[] }[] = [];
  readonly countrySuggestions = signal<string[]>([]);
  readonly regionSuggestions = signal<string[]>([]);
  readonly noticeLocked = signal(false);
  private readonly roleCell = viewChild<TemplateRef<unknown>>("roleCell");
  private readonly participantCell = viewChild<TemplateRef<unknown>>("participantCell");
  private readonly responseDateCell = viewChild<TemplateRef<unknown>>("responseDateCell");
  private readonly responseCell = viewChild<TemplateRef<unknown>>("responseCell");
  private readonly actionsCell = viewChild<TemplateRef<unknown>>("actionsCell");
  readonly rosterDisabled = computed(() => this.mode() !== "view" || !!this.statusConfirmation() || this.busy() || this.loading() || this.loadFailed());
  filterValues: Record<string, unknown> = { showInactive: false };
  readonly filterConfig: InputConfig[] = [
    { name: "showInactive", type: EzFormInputType.CHECKBOX, label: "Include inactive", row: 1 },
  ];
  readonly rosterColumnDefaults: ColDef<NoticeParticipant> = {
    flex: 1, minWidth: 150, editable: false, sortable: false, filter: false,
    resizable: true, wrapText: true, autoHeight: true, wrapHeaderText: true, autoHeaderHeight: true,
    suppressKeyboardEvent: ({ event }) => event.key === "Tab",
    cellClass: "items-start",
    cellStyle: { whiteSpace: "pre-wrap", overflowWrap: "anywhere", lineHeight: "1.4", fontWeight: "normal", paddingTop: "6px", paddingBottom: "6px" },
  };
  readonly rosterGridOptions: GridOptions<NoticeParticipant> = {
    pagination: false, defaultColDef: this.rosterColumnDefaults,
    getRowId: ({ data }) => String(data.id),
    // Native tab order includes every action within a cell; the grid must not consume those keys.
    suppressCellFocus: true, suppressColumnVirtualisation: true,
  };
  readonly rosterColumns = computed<ColDef<NoticeParticipant>[]>(() => [
    { field: "displayName", headerName: "Participant", minWidth: 140, cellRenderer: TemplateRendererComponent, cellRendererParams: { ngTemplate: this.participantCell() } },
    { field: "roleCode", headerName: "Role", minWidth: 170, flex: .7, cellRenderer: TemplateRendererComponent, cellRendererParams: { ngTemplate: this.roleCell() } },
    { field: "email", headerName: "Email", minWidth: 165, valueGetter: ({ data }) => data ? data.email || "No email" : "" },
    { field: "phone", headerName: "Phone", minWidth: 130, valueGetter: ({ data }) => data ? data.phone || "No phone" : "" },
    { colId: "relationships", headerName: "Relationships", minWidth: 165, flex: 1.4, cellRenderer: TemplateRendererComponent, cellRendererParams: { ngTemplate: this.relationshipsCell() } },
    { colId: "responseDate", headerName: "Response date", minWidth: 120, flex: .7, cellRenderer: TemplateRendererComponent, cellRendererParams: { ngTemplate: this.responseDateCell() } },
    { colId: "response", headerName: "Response", minWidth: 135, cellRenderer: TemplateRendererComponent, cellRendererParams: { ngTemplate: this.responseCell() } },
    { colId: "actions", headerName: "Actions", minWidth: 150, maxWidth: 180, cellRenderer: TemplateRendererComponent, cellRendererParams: { ngTemplate: this.actionsCell() } },
  ]);

  revealRosterColumn(event: FocusEvent, params: ICellRendererParams): void {
    // Reveal keyboard targets without moving a pointer target between mouse-down and click.
    if (event.target instanceof HTMLElement && event.target.matches(":focus-visible")) params.api.ensureColumnVisible(params.column!);
  }

  onFilterChange(event: InputChangeEvent<unknown>): void {
    if (this.rosterDisabled()) return;
    if (event.name === "showInactive") this.showInactive.set(event.value === true);
    if (!this.visiblePeople().some(p => p.id === this.historyId())) { this.historyId.set(null); this.closePreview(); }
  }

  openRosterAction(action: "history" | "participant" | "relationships" | "preview" | "response", person?: NoticeParticipant): void {
    if (action === "participant" && (!person || person.officialUserId)) {
      if (!this.rosterDisabled() && (person?.officialUserId ? this.canAssignOfficials() : this.canEdit() || this.canAssignOfficials())) this.edit(person);
      return;
    }
    if (person?.officialUserId) return;
    if (this.rosterDisabled() || !this.canRead() || (!["history", "preview"].includes(action) && !this.canEdit())) return;

    if (action === "history" && person) this.toggleHistory(person.id);
    else if (action === "participant") this.edit(person);
    else if (action === "relationships" && person) this.editRelationships(person);
    else if (action === "preview" && person) { const notice = this.previewFor(person.id); if (notice) this.preview(notice); }
    else if (action === "response") this.startResponse(person);
    if (action === "history" && this.historyId() !== null) {
      // Wait for the detail region to render before moving keyboard focus to its heading.
      requestAnimationFrame(() => this.host.nativeElement.querySelector<HTMLElement>("#participant-detail")?.focus());
    }
  }
  readonly roleOptions = [
    { code: "victim", label: "Victim" }, { code: "vnok", label: "Victim's next of kin (VNOK)" },
    { code: "representative", label: "Representative" }, { code: "support_person", label: "Support person" },
    { code: "parole_official", label: "Parole official" },
    { code: "witness", label: "Witness" }, { code: "other", label: "Other participant" },
  ];
  readonly relationshipOptions = [
    { code: "representative_for", label: "Represents" }, { code: "support_for", label: "Supports" }, { code: "next_of_kin_of", label: "Next of kin of" },
  ];
  readonly responseOptions = [
    { code: "acknowledged", label: "Acknowledged notice" }, { code: "attending", label: "Plans to attend" },
    { code: "not_attending", label: "Will not attend" }, { code: "written_statement", label: "Written statement" },
    { code: "question", label: "Question / request" }, { code: "other", label: "Other response" },
  ];
  person: NoticeParticipant = this.emptyPerson();
  address: ParticipantMailingAddress = this.emptyAddress();
  useStructuredAddress = true;
  readonly countryOptions = ADDRESS_COUNTRIES;
  readonly participantSaveAttempted = signal(false);
  readonly nonBlank = /\S/;
  notice = { onBehalfOfParticipantId: null as number | null, channelCode: "post", templateVersionId: 0 };
  response = { onBehalfOfParticipantId: null as number | null, noticeId: null as number | null, receivedLocal: "", channelCode: "email", responseCode: "acknowledged", notes: "" };

  ngOnInit(): void {
    this.destroyRef.onDestroy(() => { this.closePreview(); this.closeEditorDialog(); this.closeStatusConfirmation(); });
    effect(() => {
      const busy = this.busy();
      const canEdit = this.canEdit();
      if (!canEdit) this.closeStatusConfirmation();
      if (this.editorDialog) {
        this.editorDialog.disableClose = busy;
        if (!(this.officialEditor() ? this.canAssignOfficials() : canEdit || this.mode() === "participant" && !this.person.id && this.canAssignOfficials())) { this.closeEditorDialog(); this.mode.set("view"); this.error.set(""); }
      }
    }, { injector: this.injector });
    effect(onCleanup => {
      const id = this.proceedingId(); const read = this.canReadOfficials();
      this.officials.set([]); this.eligibleOfficials.set([]); this.officialsError.set("");
      if (!id || !read) return;
      this.officialsLoading.set(true);
      const sub = forkJoin({ members: this.officialsApi.getMembers(id), eligible: this.officialsApi.getOfficials() }).subscribe({
        next: result => { this.officials.set(result.members); this.eligibleOfficials.set(result.eligible); this.officialsLoading.set(false); },
        error: () => { this.officialsError.set("Officials could not be loaded. Reload before assigning an official."); this.officialsLoading.set(false); },
      });
      onCleanup(() => sub.unsubscribe());
    }, { injector: this.injector });
    effect(onCleanup => {
      const id = this.proceedingId();
      this.closeStatusConfirmation();
      this.closeEditorDialog();
      this.closePreview(); this.busy.set(false); this.historyId.set(null);
      if (!id || !this.canRead()) {
        this.workspace.set({ participants: [], notices: [], responses: [] });
        this.selectedId.set(null); this.mode.set("view");
        return;
      }
      this.selectedId.set(null); this.mode.set("view"); this.workspace.set({ participants: [], notices: [], responses: [] });
      this.loading.set(true); this.loadFailed.set(false); this.error.set("");
      const sub = this.api.get(id).subscribe({ next: data => { this.workspace.set(data); this.selectedId.set(data.participants.find(p => p.isActive)?.id ?? null); this.loading.set(false); },
        error: e => { this.fail(e); this.loadFailed.set(true); this.loading.set(false); } });
      onCleanup(() => sub.unsubscribe());
    }, { injector: this.injector });
  }
  reload(): void {
    const id = this.record()?.scheduledProceedingId;
    if (!id || this.busy()) return;
    this.closePreview(); this.loading.set(true);
    if (!this.canRead()) { this.loading.set(false); return; }
    this.api.get(id).pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.loading.set(false)))
      .subscribe({ next: data => { this.workspace.set(data); this.loadFailed.set(false); this.notice.templateVersionId = data.template?.versionId ?? 0; this.error.set(""); }, error: e => { this.fail(e); this.loadFailed.set(true); } });
  }
  select(id: number): void { if (this.busy()) return; this.closePreview(); this.selectedId.set(id); this.mode.set("view"); this.error.set(""); this.message.set(""); }
  name(id: number): string { return this.workspace().participants.find(p => p.id === id)?.displayName ?? "Participant unavailable"; }
  role(code: string): string { return this.roleOptions.find(r => r.code === code)?.label ?? code; }
  relationship(code: string): string { return this.relationshipOptions.find(r => r.code === code)?.label ?? code; }
  responseLabel(code: string): string { return this.responseOptions.find(r => r.code === code)?.label ?? code; }
  representedPeople(): NoticeParticipant[] {
    const ids = this.selected()?.relationships.filter(r => r.relationshipCode === "representative_for").map(r => r.relatedParticipantId) ?? [];
    return this.activePeople().filter(p => ids.includes(p.id));
  }
  relatedNotices(): ParticipantNotice[] {
    const id = this.selectedId(); const subject = this.response.onBehalfOfParticipantId;
    return this.workspace().notices.filter(n => n.participantId === id || n.onBehalfOfParticipantId === id
      || subject !== null && (n.participantId === subject || n.onBehalfOfParticipantId === subject));
  }
  noticeAddress(): string {
    const person = this.selected();
    return this.notice.channelCode === "email" ? person?.email || "Email address needed" : this.notice.channelCode === "post" ? person?.postalAddress || "Postal address needed" : "Hand delivery";
  }
  edit(person?: NoticeParticipant): void {
    if (this.busy() || (person?.officialUserId ? !this.canAssignOfficials() : person ? !this.canEdit() : !this.canEdit() && !this.canAssignOfficials())) return;
    this.officialEditor.set(!!person?.officialUserId || !this.canEdit());
    this.editingOfficialVersion = structuredClone(this.officials());
    this.selectedId.set(person?.id ?? null); this.historyId.set(null);
    this.participantSaveAttempted.set(false);
    this.person = person ? structuredClone(person) : this.emptyPerson();
    if (this.officialEditor()) this.person.roleCode = "parole_official";
    this.address = person?.mailingAddress ? structuredClone(person.mailingAddress) : this.emptyAddress();
    this.normalizeCountry(); this.normalizeRegion();
    this.useStructuredAddress = !person?.postalAddress || !!person.mailingAddress;
    this.error.set(""); this.message.set(""); this.mode.set("participant");
    this.openEditor(this.personEditor(), person?.id ? "Edit participant" : "Add participant", "hearing-participant-form", "Save participant", this.officialEditor() ? '[control-name="officialUserId"] mat-select' : '[control-name="roleCode"] mat-select');
  }
  private openEditor(template: TemplateRef<unknown> | undefined, heading: string, formId: string, saveLabel: string, autoFocus: string): void {
    this.closeEditorDialog();
    this.initializeEditorForms();
    const busy = this.busy;
    const buttonText = () => busy() ? "Saving..." : this.mode() === "notice" ? this.currentNotice() ? "Regenerate PDF" : "Generate PDF" : saveLabel;
    const saveDisabled = () => busy() || this.mode() === "notice" && !!this.generationIssue();
    const ref = this.dialog.open(EzDialogComponent, {
      width: "880px", maxWidth: "calc(100vw - 32px)", maxHeight: "90vh", autoFocus, restoreFocus: false,
      data: { heading, buttons: [], useForm: !!formId, formActions: [
        { get text() { return buttonText(); }, isSubmit: true, formId: this.mode() === "relationships" ? undefined : formId,
          get disabled() { return saveDisabled(); }, action: () => { if (this.mode() === "relationships") this.saveRelationships(); } },
        { text: "Cancel", get disabled() { return busy(); }, action: () => this.cancel() },
      ] },
    });
    this.editorDialog = ref;
    ref.componentInstance.formValid = true;
    ref.componentRef?.setInput("contentTemplate", template);
    ref.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.editorDialog !== ref) return;
      this.editorDialog = null;
      this.editorSubscriptions.unsubscribe();
      this.mode.set("view"); this.error.set(""); this.restoreRosterFocus();
    });
  }
  private initializeEditorForms(): void {
    this.editorSubscriptions = new Subscription();
    const watch = (form: FormGroup, update: () => void) => this.editorSubscriptions.add(form.valueChanges.subscribe(update));
    if (this.mode() === "participant") {
      const roleConfig = { ...selectField("roleCode", "Participant type", 1,
        this.roleOptions.filter(role => role.code === "parole_official" ? this.canAssignOfficials() && !this.person.id : this.canEdit())),
        disabled: !!this.person.officialUserId, validation: [Validators.required], validationMessages: { required: "Select a participant type." } };
      if (this.officialEditor()) {
        const choices = this.eligibleOfficials().filter(user => !this.officials().some(item => item.officialUserId === user.userId) || user.userId === this.person.officialUserId);
        if (this.person.officialUserId && !choices.some(user => user.userId === this.person.officialUserId)) choices.push({ userId: this.person.officialUserId, displayName: this.person.displayName, email: this.person.email });
        this.participantConfig = [
          ...(!this.person.id ? [roleConfig] : []),
          { ...selectField("officialUserId", this.text.officialPicker(), 2, choices.map(user => ({ code: user.userId, label: user.displayName }))), disabled: !!this.person.id,
            validation: [Validators.required], validationMessages: { required: "Select a parole official." } },
          { ...textField("displayName", "Name", 3, 300), disabled: true },
          { ...textField("email", "Email", 3, 320), disabled: true },
          { name: "isPresiding", label: "Presiding official", type: EzFormInputType.CHECKBOX, row: 4 },
        ];
        this.participantForm = editorForm(this.participantConfig, { ...this.person, officialUserId: this.person.officialUserId ?? null });
        watch(this.participantForm, () => {
          const value = this.participantForm.getRawValue();
          if (value.roleCode && value.roleCode !== "parole_official") { this.switchParticipantType(value.roleCode); return; }
          this.person.officialUserId = value.officialUserId; this.person.isPresiding = !!value.isPresiding;
          const user = choices.find(item => item.userId === value.officialUserId);
          this.participantForm.patchValue({ displayName: user?.displayName ?? "", email: user?.email ?? "" }, { emitEvent: false });
        });
        return;
      }
      this.participantConfig = noticeParticipantFields(roleConfig, !!this.person.id, this.text.firstName(), this.text.lastName());
      this.participantForm = editorForm(this.participantConfig, { ...this.person });
      watch(this.participantForm, () => {
        Object.assign(this.person, this.participantForm.getRawValue());
        if (this.person.roleCode === "parole_official") this.switchParticipantType("parole_official");
      });
      this.addressConfig = [textField("line1", "Street address", 1, 200), textField("line2", "Apartment / unit / suite (optional)", 1, 200),
        { ...textField("country", "Country", 2, 100), type: EzFormInputType.AUTO_COMPLETE,
          autoCompleteConfig: { suggestions: this.countrySuggestions, minQueryLength: 1, forceSelection: false, appendTo: this.overlayContainer.getContainerElement() } },
        textField("postalCode", "ZIP / postal code", 2, 20), textField("city", "City", 3, 100),
        { ...textField("region", "State / province", 3, 100), type: EzFormInputType.AUTO_COMPLETE,
          autoCompleteConfig: { suggestions: this.regionSuggestions, minQueryLength: 1, forceSelection: false, appendTo: this.overlayContainer.getContainerElement() } }];
      this.addressForm = editorForm(this.addressConfig, { ...this.address });
      watch(this.addressForm, () => { Object.assign(this.address, Object.fromEntries(Object.entries(this.addressForm.getRawValue()).map(([key, value]) => [key, value ?? ""]))); this.updateAddressValidation(); });
      this.updateAddressValidation();
    } else if (this.mode() === "relationships") {
      this.relationshipEditors = this.person.relationships.map(link => this.createRelationshipEditor(link));
      this.validateRelationships();
    } else if (this.mode() === "response") {
      this.responseConfig = [selectField("onBehalfOfParticipantId", "Responding for", 1, this.subjectOptions()),
        selectField("noticeId", `Related ${this.text.noticeRelated()}`, 1, this.responseNoticeOptions()),
        { ...textField("receivedLocal", "Received date and time", 2, 40, true), inputType: "datetime-local",
          validation: [Validators.required, control => {
            const date = new Date(control.value);
            return control.value && (!Number.isFinite(date.getTime()) || date > new Date()) ? { receivedDate: true } : null;
          }], validationMessages: { required: "Enter a received date and time.", receivedDate: "Enter a received date and time that is not in the future." } },
        selectField("channelCode", "Received via", 2, [{ code: "email", label: "Email" }, { code: "post", label: "Post" }, { code: "phone", label: "Phone" }, { code: "in_person", label: "In person" }, { code: "other", label: "Other" }]),
        selectField("responseCode", "Response", 3, this.responseOptions),
        { ...textField("notes", "Response details *", 4, 20000, true), type: EzFormInputType.TEXTAREA, textAreaRows: 3 }];
      this.responseForm = editorForm(this.responseConfig, { ...this.response, onBehalfOfParticipantId: 0, noticeId: 0 });
      watch(this.responseForm, () => {
        const values = this.responseForm.getRawValue(); const subject = values.onBehalfOfParticipantId || null;
        if (subject !== this.response.onBehalfOfParticipantId) { values.noticeId = 0; this.responseForm.get("noticeId")?.setValue(0, { emitEvent: false }); }
        Object.assign(this.response, values, { onBehalfOfParticipantId: subject, noticeId: values.noticeId || null });
        this.responseConfig[1].options = this.responseNoticeOptions().map(item => ({ value: item.code, viewValue: item.label }));
      });
    } else if (this.mode() === "notice") {
      this.noticeConfig = [selectField("onBehalfOfParticipantId", "For", 1, this.subjectOptions()),
        { ...selectField("channelCode", "Prepared for", 1, [{ code: "post", label: "Postal delivery" }, { code: "email", label: "Email" }, { code: "hand", label: "Hand delivery" }]), disabled: this.noticeLocked }];
      this.noticeLocked.set(!!this.currentNotice() && !this.canRegenerate(this.currentNotice()!));
      this.noticeForm = editorForm(this.noticeConfig, { ...this.notice, onBehalfOfParticipantId: this.notice.onBehalfOfParticipantId || 0 });
      watch(this.noticeForm, () => {
        const values = this.noticeForm.getRawValue();
        if ((values.onBehalfOfParticipantId || null) !== this.notice.onBehalfOfParticipantId) this.closePreview();
        Object.assign(this.notice, values, { onBehalfOfParticipantId: values.onBehalfOfParticipantId || null });
        this.noticeLocked.set(!!this.currentNotice() && !this.canRegenerate(this.currentNotice()!));
      });
    }
  }
  private switchParticipantType(roleCode: string): void {
    if (this.person.id || this.busy()) return;
    this.editorSubscriptions.unsubscribe();
    this.person = { ...this.emptyPerson(), roleCode };
    this.officialEditor.set(roleCode === "parole_official");
    this.initializeEditorForms();
  }

  reloadOfficials(): void {
    const id = this.proceedingId(); if (!id || !this.canReadOfficials() || this.busy()) return;
    this.officialsLoading.set(true);
    forkJoin({ members: this.officialsApi.getMembers(id), eligible: this.officialsApi.getOfficials() })
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({ next: result => {
        if (id !== this.proceedingId() || !this.canReadOfficials()) return;
        this.officials.set(result.members); this.eligibleOfficials.set(result.eligible); this.officialsError.set(""); this.officialsLoading.set(false);
        this.editingOfficialVersion = structuredClone(result.members);
        this.error.set(""); this.officialSaveError.set(false);
        if (this.officialEditor() && this.mode() === "participant") this.error.set("Roster refreshed. Review your selection before saving again.");
      }, error: () => { this.officialsLoading.set(false); this.officialsError.set("Officials could not be loaded. Try again."); } });
  }

  private saveOfficial(): void {
    const id = this.proceedingId();
    if (!id || !this.canAssignOfficials() || this.busy() || this.officialsLoading() || this.officialsError()) return;
    this.participantForm.markAllAsTouched();
    const value = this.participantForm.getRawValue();
    if (!value.officialUserId || this.participantForm.invalid) { this.focusInvalid(this.participantForm, "hearing-participant-form"); return; }
    if (!this.person.id && this.editingOfficialVersion.some(item => item.officialUserId === value.officialUserId)) { this.error.set("This official is already assigned. Reload the roster."); return; }
    const members = this.editingOfficialVersion.filter(item => item.officialUserId !== value.officialUserId)
      .map(item => ({ officialUserId: item.officialUserId, isPresiding: value.isPresiding ? false : !!item.isPresiding }));
    members.push({ officialUserId: value.officialUserId, isPresiding: !!value.isPresiding });
    this.saveOfficialMembers(members);
  }

  togglePresiding(person: NoticeParticipant): void {
    if (this.rosterDisabled() || this.officialsLoading() || this.officialsError() || !this.canAssignOfficials()) return;
    const current = this.officials().find(item => item.id === -person.id && item.officialUserId === person.officialUserId);
    if (!current) return;
    this.selectedId.set(person.id);
    this.editingOfficialVersion = structuredClone(this.officials());
    this.saveOfficialMembers(this.officials().map(item => ({ officialUserId: item.officialUserId,
      isPresiding: !current.isPresiding && item.id === current.id })), true);
  }

  removeOfficial(person: NoticeParticipant): void {
    if (!person.officialUserId || this.rosterDisabled() || !this.canAssignOfficials()) return;
    this.editingOfficialVersion = structuredClone(this.officials());
    this.saveOfficialMembers(this.officials().filter(item => item.officialUserId !== person.officialUserId)
      .map(item => ({ officialUserId: item.officialUserId, isPresiding: !!item.isPresiding })));
  }

  private saveOfficialMembers(members: { officialUserId: number; isPresiding: boolean }[], focusPresiding = false): void {
    const id = this.proceedingId(); if (!id || !this.canAssignOfficials() || this.busy()) return;
    this.busy.set(true); this.error.set(""); this.message.set(""); this.officialSaveError.set(false);
    this.officialsApi.saveMembers(id, members, this.editingOfficialVersion).pipe(takeUntilDestroyed(this.destroyRef), finalize(() => { if (id === this.proceedingId()) this.busy.set(false); }))
      .subscribe({ next: saved => {
        if (id !== this.proceedingId() || !this.canReadOfficials()) return;
        this.officials.set(saved); this.closeEditorDialog(); this.mode.set("view"); this.message.set("Official assignments saved."); this.restoreRosterFocus(focusPresiding);
      }, error: error => { if (id === this.proceedingId()) { this.officialSaveError.set(true); this.fail(error); } } });
  }

  private subjectOptions() { return [{ code: 0, label: `${this.selected()?.displayName} personally` }, ...this.representedPeople().map(person => ({ code: person.id, label: `On behalf of ${person.displayName}` }))]; }
  private responseNoticeOptions() { return [{ code: 0, label: "No linked notice / unsolicited response" }, ...this.relatedNotices().map(notice => ({ code: notice.id, label: `#${notice.id} · ${notice.title} · ${new Date(notice.generatedUtc).toLocaleDateString()}` }))]; }
  filterAddressSuggestions(event: { name: string; event: { query: string } }): void {
    const query = event.event.query.trim().toLocaleLowerCase();
    const countries = this.countryOptions.filter(item => item.label.toLocaleLowerCase().includes(query) || item.code.toLocaleLowerCase() === query).map(item => item.label);
    const regions = this.regionOptions().filter(item => item.label.toLocaleLowerCase().includes(query) || item.code.toLocaleLowerCase() === query).map(item => item.label);
    (event.name === "country" ? this.countrySuggestions : this.regionSuggestions).set(event.name === "country" ? countries : regions);
  }
  updateAddressValidation(): void {
    for (const config of this.addressConfig) {
      const required = this.addressStarted() && ["line1", "city", "country"].includes(config.name) || this.regionRequired() && ["region", "postalCode"].includes(config.name);
      const control = this.addressForm.get(config.name);
      control?.setValidators([...(config.validation ?? []), ...(required ? [Validators.required, Validators.pattern(/\S/)] : [])]);
      control?.updateValueAndValidity({ emitEvent: false });
      if (["country", "region"].includes(config.name)) config.label = `${config.name === "country" ? "Country" : "State / province"}${required ? " *" : ""}`;
    }
  }
  private createRelationshipEditor(link: NoticeParticipant['relationships'][number]) {
    const config = [selectField("relationshipCode", "Relationship", 1, this.relationshipOptions),
      { ...selectField("relatedParticipantId", "Participant", 1, this.workspace().participants.filter(person => person.id !== this.person.id).map(person => ({ code: person.id, label: `${person.displayName}${person.isActive ? "" : " (inactive)"}` }))),
        validation: [Validators.required], validationMessages: { required: "Select another participant, or remove this relationship.", relationship: "Check this relationship." } }];
    const form = editorForm(config, { ...link, relatedParticipantId: link.relatedParticipantId || null });
    this.editorSubscriptions.add(form.valueChanges.subscribe(() => { Object.assign(link, form.getRawValue()); this.validateRelationships(); }));
    return { link, config, form };
  }
  validateRelationships(): void {
    this.relationshipEditors.forEach((editor, index) => {
      const error = this.relationshipError(index);
      editor.config[1].validationMessages = { relationship: error };
      editor.form.get("relatedParticipantId")?.setErrors(error ? { relationship: true } : null, { emitEvent: false });
    });
  }
  removeRelationship(index: number): void { this.person.relationships.splice(index, 1); this.relationshipEditors.splice(index, 1); this.validateRelationships(); }
  private focusInvalid(form: FormGroup, formId: string): void {
    const name = Object.keys(form.controls).find(key => form.controls[key].invalid);
    if (name) document.getElementById(formId)?.querySelector<HTMLElement>(`[control-name="${name}"] input, [control-name="${name}"] textarea, [control-name="${name}"] mat-select`)?.focus();
  }
  editRelationships(person: NoticeParticipant): void {
    if (this.busy() || !this.canEdit()) return;
    this.person = structuredClone(person);
    this.selectedId.set(person.id); this.historyId.set(null); this.participantSaveAttempted.set(false);
    this.error.set(""); this.message.set(""); this.mode.set("relationships");
    this.openEditor(this.relationshipEditor(), "Edit relationships", "hearing-relationships-form", "Save relationships", this.workspace().participants.length > 1 ? '[data-testid="add-relationship"]' : "first-tabbable");
  }
  saveRelationships(): void {
    const id = this.proceedingId();
    if (!id || !this.person.id || !this.canEdit() || this.busy()) return;
    this.participantSaveAttempted.set(true); this.relationshipEditors.forEach(editor => editor.form.markAllAsTouched()); this.validateRelationships();
    const invalidIndex = this.person.relationships.findIndex((_, index) => !!this.relationshipError(index));
    if (invalidIndex >= 0) {
      this.focusInvalid(this.relationshipEditors[invalidIndex].form, `hearing-relationship-${invalidIndex}`);
      return;
    }
    // Submit the exact saved contact/name/address values and version; only links are editable here.
    this.perform(this.api.saveParticipant(id, structuredClone(this.person)), person => this.participantSaved(person, "Relationships saved."));
  }
  private participantSaved(person: NoticeParticipant, message: string): void {
    this.workspace.update(w => ({ ...w, participants: [...w.participants.filter(p => p.id !== person.id), person].sort((a, b) => a.displayName.localeCompare(b.displayName)) }));
    if (!person.isActive) this.showInactive.set(true);
    this.filterValues = { showInactive: this.showInactive() };
    this.selectedId.set(person.id); this.mode.set("view"); this.message.set(message);
    this.closeEditorDialog(); this.restoreRosterFocus();
  }
  private closeEditorDialog(): void {
    const ref = this.editorDialog;
    this.editorDialog = null;
    this.editorSubscriptions.unsubscribe();
    ref?.close();
  }
  addRelationship(): void {
    const link = { relatedParticipantId: 0, relationshipCode: "representative_for" };
    this.person.relationships.push(link); this.relationshipEditors.push(this.createRelationshipEditor(link)); this.validateRelationships();
  }
  startNotice(person?: NoticeParticipant): void {
    if (this.busy() || !this.canEdit() || !(person ?? this.selected())?.isActive) return;
    this.closePreview();
    if (person) this.select(person.id);
    this.historyId.set(null);
    this.notice = { onBehalfOfParticipantId: null, channelCode: this.selected()?.postalAddress ? "post" : this.selected()?.email ? "email" : "hand", templateVersionId: this.workspace().template?.versionId ?? 0 };
    this.error.set(""); this.message.set(""); this.mode.set("notice");
    this.openEditor(this.noticeEditor(), `Prepare ${this.text.noticePrepare()}`, "hearing-notice-form", "Generate PDF", '[control-name="onBehalfOfParticipantId"] mat-select');
  }
  startResponse(person?: NoticeParticipant): void {
    if (this.busy() || !this.canEdit()) return;
    if (person) this.select(person.id);
    this.historyId.set(null);
    const now = new Date(); now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    this.response = { onBehalfOfParticipantId: null, noticeId: null, receivedLocal: now.toISOString().slice(0, 16), channelCode: "email", responseCode: "acknowledged", notes: "" };
    this.error.set(""); this.message.set(""); this.mode.set("response");
    this.openEditor(this.responseEditor(), "Record response", "hearing-response-form", "Record response", '[control-name="onBehalfOfParticipantId"] mat-select');
  }
  cancel(): void { if (this.busy()) return; this.closePreview(); this.closeEditorDialog(); this.mode.set("view"); this.error.set(""); this.restoreRosterFocus(); }
  closeHistory(): void { this.closePreview(); this.historyId.set(null); this.restoreRosterFocus(); }
  private restoreRosterFocus(presiding = false): void {
    const id = this.selectedId();
    afterNextRender(() => requestAnimationFrame(() => requestAnimationFrame(() => {
      if (this.mode() !== "view" || this.historyId() !== null) return;
      this.host.nativeElement.querySelector<HTMLElement>(id
        ? `[${presiding ? "data-presiding-participant-id" : "data-participant-id"}="${id}"] button:not(:disabled)` : '[data-testid="add-participant"] button')?.focus();
    })), { injector: this.injector });
  }
  savePerson(form?: FormGroup, addressForm?: FormGroup): void {
    if (this.officialEditor()) { this.saveOfficial(); return; }
    const id = this.record()?.scheduledProceedingId;
    if (!id || !this.canEdit() || this.busy()) return;
    this.participantSaveAttempted.set(true);
    form?.markAllAsTouched(); if (this.useStructuredAddress) addressForm?.markAllAsTouched();
    const invalidRelationship = this.person.relationships.findIndex((_, i) => !!this.relationshipError(i));
    if (form?.invalid || this.useStructuredAddress && addressForm?.invalid || !this.person.firstName?.trim() || !this.person.lastName?.trim() || !this.roleOptions.some(r => r.code === this.person.roleCode)
      || new FormControl(this.person.email, Validators.email).invalid || this.addressIncomplete() || invalidRelationship >= 0) {
      if (form?.invalid) this.focusInvalid(form, "hearing-participant-form");
      else if (addressForm?.invalid) this.focusInvalid(addressForm, "hearing-address-form");
      return;
    }
    const mailingAddress = this.useStructuredAddress ? { ...this.address,
      country: normalizeAddressCountry(this.address.country), region: normalizeAddressRegion(this.address.country, this.address.region) } : null;
    this.perform(this.api.saveParticipant(id, { ...this.person, firstName: this.person.firstName.trim(), lastName: this.person.lastName.trim(),
      displayName: `${this.person.firstName.trim()} ${this.person.lastName.trim()}`, mailingAddress }), person => this.participantSaved(person, "Participant saved."));
  }
  generate(): void {
    const id = this.record()?.scheduledProceedingId; const participantId = this.selectedId();
    if (!id || !participantId || !this.canEdit() || this.busy()) return;
    const issue = this.generationIssue();
    if (issue) { this.error.set(issue); return; }
    if (!this.notice.templateVersionId || this.notice.templateVersionId !== this.workspace().template?.versionId) {
      this.error.set("Review the current notice template before generating."); return;
    }
    const existing = this.currentNotice();
    if (existing && (!this.canRegenerate(existing) || this.previewNoticeId() !== existing.id)) {
      this.error.set(this.canRegenerate(existing) ? "Preview the saved PDF before regenerating this notice." : "This notice is locked and cannot be regenerated."); return;
    }
    this.perform(this.api.generate(id, { ...this.notice, participantId,
      ...(existing ? { replacesNoticeId: existing.id, expectedVersion: existing.version ?? 1 } : {}) }), notice => {
      this.closePreview();
      this.workspace.update(w => ({ ...w, notices: [notice, ...w.notices.map(n => n.id === existing?.id ? { ...n, statusCode: "superseded", version: (n.version ?? 1) + 1 } : n)] })); this.mode.set("view");
      this.closeEditorDialog(); this.historyId.set(participantId);
      this.message.set("Notice generated. Download the saved PDF from this participant's history. Delivery has not been recorded.");
    });
  }
  saveResponse(form?: FormGroup): void {
    const id = this.record()?.scheduledProceedingId; const participantId = this.selectedId();
    if (!id || !participantId || !this.canEdit() || this.busy()) return;
    form?.markAllAsTouched();
    if (form?.invalid || !this.response.notes.trim()) {
      this.error.set("Complete the required response fields.");
      if (form) this.focusInvalid(form, "hearing-response-form"); return;
    }
    const received = new Date(this.response.receivedLocal);
    if (!Number.isFinite(received.getTime()) || received > new Date()) { this.error.set("Enter a received date and time that is not in the future."); return; }
    this.perform(this.api.respond(id, { ...this.response, participantId, receivedUtc: received.toISOString() }), response => {
      this.workspace.update(w => ({ ...w, responses: [response, ...w.responses] })); this.mode.set("view"); this.message.set("Response recorded."); this.closeEditorDialog(); this.restoreRosterFocus();
    });
  }
  reviewNotice(notice: ParticipantNotice): void {
    if (this.busy() || !this.canEdit()) return;
    const person = this.workspace().participants.find(p => p.id === notice.participantId);
    if (!person?.isActive || !this.canRegenerate(notice)) return;
    this.startNotice(person);
    this.notice.onBehalfOfParticipantId = notice.onBehalfOfParticipantId;
    this.notice.channelCode = notice.channelCode;
    this.noticeForm.patchValue({ onBehalfOfParticipantId: notice.onBehalfOfParticipantId || 0, channelCode: notice.channelCode });
    this.preview(notice);
  }
  currentNotice(): ParticipantNotice | null {
    return this.workspace().notices.find(n => n.participantId === this.selectedId()
      && n.onBehalfOfParticipantId === this.notice.onBehalfOfParticipantId && n.statusCode !== "superseded") ?? null;
  }
  canRegenerate(notice: ParticipantNotice): boolean {
    return !notice.hasServiceAttempts && (notice.statusCode ?? "generated") === "generated" && !notice.sentUtc && !notice.deliveredUtc && !notice.returnedUtc;
  }
  canReviewNotice(notice: ParticipantNotice): boolean {
    return this.canRegenerate(notice) && !!this.workspace().participants.find(person => person.id === notice.participantId)?.isActive;
  }
  previewFor(participantId: number): ParticipantNotice | null {
    const notices = this.noticesFor(participantId).filter(n => n.statusCode !== "superseded")
      .sort((a, b) => b.generatedUtc.localeCompare(a.generatedUtc) || b.id - a.id);
    return notices.find(n => n.participantId === participantId && n.onBehalfOfParticipantId === null) ?? notices[0] ?? null;
  }
  generationIssue(): string {
    const current = this.currentNotice();
    if (!this.selected()?.isActive) return "Inactive participants cannot receive new notices.";
    if (current && !this.canRegenerate(current)) return "This notice is locked and cannot be regenerated.";
    if (!this.workspace().template || !this.notice.templateVersionId) return "No published participant notice template is available. Publish a template, then reload to generate a notice.";
    if (this.notice.channelCode === "email" && (!this.selected()?.email || new FormControl(this.selected()?.email, Validators.email).invalid)) return "Record a valid email address for this participant before generating.";
    if (this.notice.channelCode === "post" && !this.selected()?.postalAddress) return "Record a mailing address for this participant before generating.";
    if (current && this.previewNoticeId() !== current.id) return "Preview the saved PDF before regenerating this notice.";
    return "";
  }
  confirmSent(notice: ParticipantNotice): void {
    if (this.busy() || !this.canEdit() || !this.canRegenerate(notice) || this.statusDialog) return;
    const snapshot = structuredClone(notice); const hearing = this.proceedingId();
    this.statusConfirmation.set(snapshot);
    const ref = this.dialog.open(EzDialogComponent, { width: "560px", maxWidth: "calc(100vw - 32px)", autoFocus: "first-tabbable",
      data: { heading: "Record notice as sent", headerStyle: "warning", buttons: [
        { text: "Cancel", action: () => this.closeStatusConfirmation() },
        { text: "Mark sent", action: () => { this.closeStatusConfirmation(); if (this.proceedingId() === hearing) this.updateNoticeStatus(snapshot, "sent"); } },
      ] } });
    this.statusDialog = ref; ref.componentRef?.setInput("contentTemplate", this.statusConfirmationContent());
    ref.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => { if (this.statusDialog === ref) { this.statusDialog = null; this.statusConfirmation.set(null); } });
  }
  private closeStatusConfirmation(): void { const ref = this.statusDialog; this.statusDialog = null; this.statusConfirmation.set(null); ref?.close(); }
  preview(notice: ParticipantNotice): void {
    const id = this.proceedingId();
    if (!id || this.busy() || !this.canRead()) return;
    // Open synchronously from the click so authenticated fetching does not trigger popup blocking.
    const tab = window.open("about:blank", "_blank");
    if (!tab) { this.error.set("Allow a new tab to preview the saved notice, then try again."); return; }
    tab.opener = null;
    tab.document.title = "Saved notice preview";
    tab.document.body.textContent = "Loading saved notice…";
    this.busy.set(true); this.error.set("");
    let opened = false;
    this.api.download(id, notice.id).pipe(takeUntilDestroyed(this.destroyRef), finalize(() => {
      if (!opened) tab.close();
      if (this.proceedingId() === id) this.busy.set(false);
    })).subscribe({
      next: blob => {
        if (tab.closed || this.proceedingId() !== id || !this.canRead()) return;
        const url = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
        tab.location.replace(url); opened = true; this.previewNoticeId.set(notice.id);
        // Keep the PDF usable after switching roster/history views; release it when its tab closes.
        const cleanup = setInterval(() => { if (tab.closed) { URL.revokeObjectURL(url); clearInterval(cleanup); } }, 1000);
      },
      error: error => { if (this.proceedingId() === id) this.fail(error); },
    });
  }
  closePreview(): void { this.previewNoticeId.set(null); }
  updateNoticeStatus(notice: ParticipantNotice, status: "sent" | "delivered" | "returned"): void {
    const id = this.proceedingId();
    if (!id || this.busy() || !this.canEdit()) return;
    if (!(this.canRegenerate(notice) && status === "sent" || notice.statusCode === "sent" && status !== "sent")) return;
    this.perform(this.api.updateNoticeStatus(id, notice.id, status, notice.version ?? 1), updated => {
      this.workspace.update(w => ({ ...w, notices: w.notices.map(n => n.id === updated.id ? updated : n) }));
      this.message.set(`Notice marked ${status}. Its saved content is locked.`);
    });
  }
  download(notice: ParticipantNotice): void {
    const id = this.record()?.scheduledProceedingId;
    if (!id || this.busy()) return;
    this.perform(this.api.download(id, notice.id), blob => {
      const url = URL.createObjectURL(blob); const link = document.createElement("a");
      link.href = url; link.download = `hearing-notice-${notice.id}.pdf`; link.click(); setTimeout(() => URL.revokeObjectURL(url), 1000);
    });
  }
  private perform<T>(request: Observable<T>, next: (result: T) => void): void {
    const hearingId = this.proceedingId();
    this.busy.set(true); this.error.set(""); this.message.set(""); this.officialSaveError.set(false);
    request.pipe(takeUntilDestroyed(this.destroyRef), finalize(() => {
      if (this.proceedingId() === hearingId) this.busy.set(false);
    })).subscribe({
      next: result => { if (this.proceedingId() === hearingId && this.canRead()) next(result); },
      error: e => { if (this.proceedingId() === hearingId) this.fail(e); },
    });
  }
  private fail(error: { status?: number; error?: { message?: string; Message?: string; detail?: string } }): void {
    this.error.set(error.status === 403 ? "You do not have permission to access these participant records."
      : error.status === 409 ? "This record changed. Reload the roster, then review your changes before saving again."
      : error.error?.message || error.error?.Message || error.error?.detail || "The participant workspace could not be updated. Please try again.");
  }
  noticesFor(id: number) { return this.workspace().notices.filter(n => n.participantId === id || n.onBehalfOfParticipantId === id); }
  responsesFor(id: number) { return this.workspace().responses.filter(r => r.participantId === id || r.onBehalfOfParticipantId === id).sort((a, b) => b.receivedUtc.localeCompare(a.receivedUtc)); }
  linksFor(person: NoticeParticipant): string[] {
    const outgoing = person.relationships.map(link => `${this.relationship(link.relationshipCode)} ${this.name(link.relatedParticipantId)}`);
    const incoming = this.workspace().participants.flatMap(other => other.relationships.filter(link => link.relatedParticipantId === person.id)
      .map(link => `${link.relationshipCode === "representative_for" ? "Represented by" : link.relationshipCode === "support_for" ? "Supported by" : "Next of kin"}: ${other.displayName}${other.isActive ? "" : " (inactive)"}`));
    return [...outgoing, ...incoming];
  }
  toggleHistory(id: number): void {
    if (this.busy() || this.mode() !== "view") return;
    this.select(id); this.historyId.update(current => current === id ? null : id);
  }
  addressStarted(): boolean { return [this.address.line1, this.address.line2, this.address.city, this.address.region, this.address.postalCode].some(v => v.trim().length > 0); }
  addressIncomplete(): boolean {
    return this.useStructuredAddress && this.addressStarted() && (!this.address.line1.trim() || !this.address.city.trim() || !this.address.country.trim()
      || this.regionRequired() && (!this.address.region.trim() || !this.address.postalCode.trim()));
  }
  relationshipError(index: number): string {
    const link = this.person.relationships[index];
    if (!link) return "";
    const other = this.workspace().participants.find(p => p.id === link.relatedParticipantId);
    if (!other || other.id === this.person.id) return "Select another participant, or remove this relationship.";
    if (this.person.relationships.some((l, i) => i !== index && l.relatedParticipantId === link.relatedParticipantId && l.relationshipCode === link.relationshipCode))
      return "This relationship is already listed. Remove the duplicate.";
    if (link.relationshipCode === "support_for" && !["victim", "vnok"].includes(other.roleCode)) return "Select a victim or VNOK for a support relationship.";
    if (link.relationshipCode === "next_of_kin_of" && (this.person.roleCode !== "vnok" || other.roleCode !== "victim")) return "Next of kin must link a VNOK participant to a victim.";
    return "";
  }
  regionOptions() { return addressRegions(this.address.country); }
  normalizeCountry(): void { this.address.country = normalizeAddressCountry(this.address.country); }
  normalizeRegion(): void {
    const code = normalizeAddressRegion(this.address.country, this.address.region);
    this.address.region = this.regionOptions().find(region => region.code === code)?.label ?? code;
  }
  regionRequired(): boolean { return this.addressStarted() && ["US", "CA"].includes(addressCountryCode(this.address.country) ?? ""); }
  private emptyAddress(): ParticipantMailingAddress { return { line1: "", line2: "", city: "", region: "", postalCode: "", country: "United States" }; }
  private emptyPerson(): NoticeParticipant { return { id: 0, displayName: "", firstName: "", lastName: "", roleCode: "victim", email: "", postalAddress: "", phone: "", isActive: true, version: 0, relationships: [] }; }
}
